/**
 * Registry binding configuration test simulators to workflow helpers and routes.
 */

import { CONFIGURATION_RULE_TEST_MODULES } from '../configurationTestSimulationConfig';
import { getLatestSavedRule } from './shared';
import { getModuleSimulator, MODULE_SIMULATORS } from './modules';

import { buildSampleValidationRules } from '../validationConfig';
import {
  getEditRuleSource as getValidationEditRuleSource,
  getPublishedRule as getValidationPublishedRule,
  mergeRulesAfterActivate as mergeValidationRulesAfterActivate,
  appendTestHistory as appendValidationTestHistory,
} from '../validationRuleWorkflow';

import { buildSampleWorkflowRules } from '../workflowConfig';
import {
  getEditRuleSource as getWorkflowEditRuleSource,
  getPublishedRule as getWorkflowPublishedRule,
  mergeRulesAfterActivate as mergeWorkflowRulesAfterActivate,
  appendTestHistory as appendWorkflowTestHistory,
} from '../workflowRuleWorkflow';

import { buildSampleContentControlRules } from '../contentControlConfig';
import {
  getEditRuleSource as getContentControlEditRuleSource,
  getPublishedRule as getContentControlPublishedRule,
  mergeRulesAfterActivate as mergeContentControlRulesAfterActivate,
  appendTestHistory as appendContentControlTestHistory,
} from '../contentControlRuleWorkflow';

import { buildSamplePublishingRules } from '../publishingConfig';
import {
  getEditRuleSource as getPublishingEditRuleSource,
  getPublishedRule as getPublishingPublishedRule,
  mergeRulesAfterActivate as mergePublishingRulesAfterActivate,
  appendTestHistory as appendPublishingTestHistory,
} from '../publishingRuleWorkflow';

import { buildSampleDocumentRules, normalizeDocumentRuleForForm } from '../documentsConfig';

import { buildSampleCaseActionRules } from '../caseActionsConfig';
import {
  getEditRuleSource as getCaseActionEditRuleSource,
  getPublishedRule as getCaseActionPublishedRule,
  mergeRulesAfterActivate as mergeCaseActionRulesAfterActivate,
  appendTestHistory as appendCaseActionTestHistory,
} from '../caseActionRuleWorkflow';

import { buildSampleLimitRules } from '../limitsThresholdsConfig';
import {
  getEditRuleSource as getLimitEditRuleSource,
  getPublishedRule as getLimitPublishedRule,
  mergeRulesAfterActivate as mergeLimitRulesAfterActivate,
  appendTestHistory as appendLimitTestHistory,
} from '../limitRuleWorkflow';

import { buildSampleAutomationRules } from '../automationConfig';
import {
  getEditRuleSource as getAutomationEditRuleSource,
  getPublishedRule as getAutomationPublishedRule,
  mergeRulesAfterActivate as mergeAutomationRulesAfterActivate,
  appendTestHistory as appendAutomationTestHistory,
} from '../automationRuleWorkflow';

import { buildSampleLifecycleRules } from '../lifecycleConfig';
import {
  getEditRuleSource as getLifecycleEditRuleSource,
  getPublishedRule as getLifecyclePublishedRule,
  mergeRulesAfterActivate as mergeLifecycleRulesAfterActivate,
  appendTestHistory as appendLifecycleTestHistory,
} from '../lifecycleRuleWorkflow';

import { buildSampleRetentionRules } from '../retentionConfig';
import {
  getEditRuleSource as getRetentionEditRuleSource,
  getPublishedRule as getRetentionPublishedRule,
  mergeRulesAfterActivate as mergeRetentionRulesAfterActivate,
  appendTestHistory as appendRetentionTestHistory,
} from '../retentionRuleWorkflow';

import { buildSampleFlaggingRules } from '../flaggingConfig';
import {
  getEditRuleSource as getFlaggingEditRuleSource,
  getPublishedRule as getFlaggingPublishedRule,
  mergeRulesAfterActivate as mergeFlaggingRulesAfterActivate,
  appendTestHistory as appendFlaggingTestHistory,
} from '../flaggingRuleWorkflow';

import { buildSampleNotificationRules } from '../notificationsConfig';
import {
  getEditRuleSource as getNotificationEditRuleSource,
  getPublishedRule as getNotificationPublishedRule,
  mergeRulesAfterActivate as mergeNotificationRulesAfterActivate,
  appendTestHistory as appendNotificationTestHistory,
} from '../notificationRuleWorkflow';

function bindWorkflow(simulator, routeMeta, workflow, sampleBuilder, { normalize } = {}) {
  const normalizeRule = normalize || ((r) => r);
  return {
    ...routeMeta,
    simulator,
    buildSampleRules: sampleBuilder,
    getLatestSavedRule: (ruleFromState, allRules, ruleId) =>
      getLatestSavedRule(ruleFromState, allRules, ruleId, {
        getEditRuleSource: workflow.getEditRuleSource,
        normalize: normalizeRule,
      }),
    getPublishedRule: workflow.getPublishedRule,
    mergeRulesAfterActivate: workflow.mergeRulesAfterActivate,
    appendTestHistory: workflow.appendTestHistory,
    isDocuments: false,
  };
}

const WORKFLOW_BINDINGS = {
  validation: bindWorkflow(
    MODULE_SIMULATORS.validation,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'validation'),
    {
      getEditRuleSource: getValidationEditRuleSource,
      getPublishedRule: getValidationPublishedRule,
      mergeRulesAfterActivate: mergeValidationRulesAfterActivate,
      appendTestHistory: appendValidationTestHistory,
    },
    buildSampleValidationRules,
  ),
  workflow: bindWorkflow(
    MODULE_SIMULATORS.workflow,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'workflow'),
    {
      getEditRuleSource: getWorkflowEditRuleSource,
      getPublishedRule: getWorkflowPublishedRule,
      mergeRulesAfterActivate: mergeWorkflowRulesAfterActivate,
      appendTestHistory: appendWorkflowTestHistory,
    },
    buildSampleWorkflowRules,
  ),
  'content-control': bindWorkflow(
    MODULE_SIMULATORS['content-control'],
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'content-control'),
    {
      getEditRuleSource: getContentControlEditRuleSource,
      getPublishedRule: getContentControlPublishedRule,
      mergeRulesAfterActivate: mergeContentControlRulesAfterActivate,
      appendTestHistory: appendContentControlTestHistory,
    },
    buildSampleContentControlRules,
  ),
  publishing: bindWorkflow(
    MODULE_SIMULATORS.publishing,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'publishing'),
    {
      getEditRuleSource: getPublishingEditRuleSource,
      getPublishedRule: getPublishingPublishedRule,
      mergeRulesAfterActivate: mergePublishingRulesAfterActivate,
      appendTestHistory: appendPublishingTestHistory,
    },
    buildSamplePublishingRules,
  ),
  documents: {
    ...CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'documents'),
    simulator: MODULE_SIMULATORS.documents,
    buildSampleRules: buildSampleDocumentRules,
    getLatestSavedRule: (ruleFromState, allRules, ruleId) => {
      if (ruleFromState?.id) {
        const fromList = (allRules || []).find((r) => r.id === ruleFromState.id);
        return normalizeDocumentRuleForForm(fromList || ruleFromState);
      }
      if (ruleId) {
        const found = (allRules || []).find((r) => r.id === ruleId);
        if (found) return normalizeDocumentRuleForForm(found);
      }
      return null;
    },
    getPublishedRule: (rule) => rule,
    mergeRulesAfterActivate: null,
    appendTestHistory: null,
    isDocuments: true,
  },
  'case-actions': bindWorkflow(
    MODULE_SIMULATORS['case-actions'],
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'case-actions'),
    {
      getEditRuleSource: getCaseActionEditRuleSource,
      getPublishedRule: getCaseActionPublishedRule,
      mergeRulesAfterActivate: mergeCaseActionRulesAfterActivate,
      appendTestHistory: appendCaseActionTestHistory,
    },
    buildSampleCaseActionRules,
  ),
  limits: bindWorkflow(
    MODULE_SIMULATORS.limits,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'limits'),
    {
      getEditRuleSource: getLimitEditRuleSource,
      getPublishedRule: getLimitPublishedRule,
      mergeRulesAfterActivate: mergeLimitRulesAfterActivate,
      appendTestHistory: appendLimitTestHistory,
    },
    buildSampleLimitRules,
  ),
  automation: bindWorkflow(
    MODULE_SIMULATORS.automation,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'automation'),
    {
      getEditRuleSource: getAutomationEditRuleSource,
      getPublishedRule: getAutomationPublishedRule,
      mergeRulesAfterActivate: mergeAutomationRulesAfterActivate,
      appendTestHistory: appendAutomationTestHistory,
    },
    buildSampleAutomationRules,
  ),
  lifecycle: bindWorkflow(
    MODULE_SIMULATORS.lifecycle,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'lifecycle'),
    {
      getEditRuleSource: getLifecycleEditRuleSource,
      getPublishedRule: getLifecyclePublishedRule,
      mergeRulesAfterActivate: mergeLifecycleRulesAfterActivate,
      appendTestHistory: appendLifecycleTestHistory,
    },
    buildSampleLifecycleRules,
  ),
  retention: bindWorkflow(
    MODULE_SIMULATORS.retention,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'retention'),
    {
      getEditRuleSource: getRetentionEditRuleSource,
      getPublishedRule: getRetentionPublishedRule,
      mergeRulesAfterActivate: mergeRetentionRulesAfterActivate,
      appendTestHistory: appendRetentionTestHistory,
    },
    buildSampleRetentionRules,
  ),
  reporting: bindWorkflow(
    MODULE_SIMULATORS.reporting,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'reporting'),
    {
      getEditRuleSource: getFlaggingEditRuleSource,
      getPublishedRule: getFlaggingPublishedRule,
      mergeRulesAfterActivate: mergeFlaggingRulesAfterActivate,
      appendTestHistory: appendFlaggingTestHistory,
    },
    buildSampleFlaggingRules,
  ),
  notifications: bindWorkflow(
    MODULE_SIMULATORS.notifications,
    CONFIGURATION_RULE_TEST_MODULES.find((m) => m.key === 'notifications'),
    {
      getEditRuleSource: getNotificationEditRuleSource,
      getPublishedRule: getNotificationPublishedRule,
      mergeRulesAfterActivate: mergeNotificationRulesAfterActivate,
      appendTestHistory: appendNotificationTestHistory,
    },
    buildSampleNotificationRules,
  ),
};

export function getConfigurationTestRegistryEntry(moduleKey) {
  return WORKFLOW_BINDINGS[moduleKey] || null;
}

export function enrichRuleSummary(rule, simulator) {
  const rows = simulator.getRuleSummary(rule || {});
  const statusLabel = rule?.status
    ? String(rule.status).charAt(0).toUpperCase() + String(rule.status).slice(1)
    : '—';
  return [
    { label: 'Rule ID', value: rule?.id || '—' },
    { label: 'Rule Version Tested', value: rule?.version ? `v${rule.version}` : '—' },
    { label: 'Status', value: statusLabel },
    ...rows,
  ];
}

export { getModuleSimulator };
